import { AHWidgetTitle, Box } from "arkh";
import React, { useMemo } from "react";

import { BaseWidgetProps } from "../BaseProps";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";

import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { WidgetIssueOverviewData_widgetIssueOverview$key } from "./__generated__/WidgetIssueOverviewData_widgetIssueOverview.graphql";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetIssueOverviewIssueChangedSubscription } from "./__generated__/WidgetIssueOverviewIssueChangedSubscription.graphql";
import { WidgetIssueOverview_widgetIssueOverview$key } from "./__generated__/WidgetIssueOverview_widgetIssueOverview.graphql";
import { WidgetIssueOverviewQuery } from "./__generated__/WidgetIssueOverviewQuery.graphql";
import { WidgetIssueOverviewIssueCreatedSubscription } from "./__generated__/WidgetIssueOverviewIssueCreatedSubscription.graphql";
import {
  WidgetIssueOverviewList,
  NoIssuesComponent,
} from "./WidgetIssueOverviewList";
import { useSetWidgetFocus } from "../useSetFocus";
import { usePollFetchKey } from "../usePollFetchKey";
import { WidgetIssueOverviewHighlightedQuery } from "./__generated__/WidgetIssueOverviewHighlightedQuery.graphql";
import { useEvent } from "../useEvent";

export const WidgetIssueOverview = (
  props: {
    widget: WidgetIssueOverview_widgetIssueOverview$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetIssueOverview_widgetIssueOverview on WidgetIssueOverview {
        id
        title
      }
    `,
    props.widget
  );
  const wrapperStyle = useMemo(() => ({ flex: 1 }), []);

  const fk = usePollFetchKey(props.interactive === false ? null : 40000);

  const data = useLazyLoadQuery<WidgetIssueOverviewQuery>(
    graphql`
      query WidgetIssueOverviewQuery($id: ID!) {
        widgetIssueOverview(id: $id) {
          id
          title
          ...WidgetIssueOverviewData_widgetIssueOverview
        }
      }
    `,
    {
      id: widget.id,
    },
    {
      fetchKey: fk,
      fetchPolicy: "store-and-network",
    }
  );

  if (data?.widgetIssueOverview) {
    return (
      <WidgetIssueOverviewDataComponent
        {...props}
        widget={data.widgetIssueOverview}
      />
    );
  }
  return (
    <Box style={wrapperStyle} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
    </Box>
  );
};

export const WidgetIssueOverviewDataComponent = (
  props: {
    widget: WidgetIssueOverviewData_widgetIssueOverview$key;
  } & BaseWidgetProps
) => {
  const highlightedQuery =
    useLazyLoadQuery<WidgetIssueOverviewHighlightedQuery>(
      graphql`
        query WidgetIssueOverviewHighlightedQuery {
          store {
            activeIssueId
          }
        }
      `,
      {}
    );
  const widget = useFragment(
    graphql`
      fragment WidgetIssueOverviewData_widgetIssueOverview on WidgetIssueOverview {
        id
        title
        overviewSize
        teams {
          id
        }
        issues {
          id
          ...WidgetIssueOverviewList_issue
        }
      }
    `,
    props.widget
  );

  const issues = useMemo(() => widget.issues ?? [], [widget.issues]);
  const issueIds = useMemo(() => issues.map(({ id }) => id) ?? [], [issues]);
  const issueIdKey = useMemo(
    () => issueIds.slice().sort().join(""),
    [issueIds]
  );
  const memoizedIssueIds = useMemo(
    () => issueIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [issueIdKey]
  );

  useSubscriptionWithSkip<WidgetIssueOverviewIssueChangedSubscription>(
    useMemo(
      () => ({
        variables: { issueIds: memoizedIssueIds },
        subscription: graphql`
          subscription WidgetIssueOverviewIssueChangedSubscription(
            $issueIds: [ID!]!
          ) {
            issuesChangedByIds(issueIds: $issueIds) {
              id
              title
              resolved
              ...WidgetIssueOverviewList_issue
            }
          }
        `,
      }),
      [memoizedIssueIds]
    ),
    {
      skip: props.interactive === false,
    }
  );

  const teamIds = useMemo(
    () => widget?.teams?.map(({ id }) => id) ?? [],
    [widget.teams]
  );
  useSubscriptionWithSkip<WidgetIssueOverviewIssueCreatedSubscription>(
    useMemo(
      () => ({
        variables: { teamIds },
        updater: (store, data) => {
          const w = store.get(widget.id);
          if (!w || !data.issueCreated) {
            return;
          }
          const issues = w.getLinkedRecords("issues");
          w.setLinkedRecords(
            [store.get(data.issueCreated.id)!, ...(issues ?? [])],
            "issues"
          );
        },
        subscription: graphql`
          subscription WidgetIssueOverviewIssueCreatedSubscription(
            $teamIds: [ID!]!
          ) {
            issueCreated(teamIds: $teamIds) {
              id
              ...WidgetIssueOverviewList_issue
            }
          }
        `,
      }),
      [teamIds, widget.id]
    ),
    {
      skip: props.interactive === false,
    }
  );

  const wrapperStyle = useMemo(() => ({ flex: 1 }), []);

  const { onEvent } = props;

  const focus = useSetWidgetFocus(widget.id, props.storecastId);
  const onPressItem = useEvent((id: string) => {
    if (props.mode === "FOCUS" && props.highlightedItemId !== id) {
      focus(id);
    } else {
      onEvent?.({
        kind: "Issue",
        issueId: id,
        widgetId: widget.id,
        expanded: true,
      });
    }
  });

  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);

  return (
    <Box style={wrapperStyle} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      {issues.length > 0 ? (
        <WidgetIssueOverviewList
          issues={widget.issues}
          isFocused={props.isFocused}
          mode={props.mode}
          onPressItem={onPressItem}
          setScroll={setScroll}
          interactive={props.interactive}
          highlightedItemId={
            props.highlightedItemId ??
            highlightedQuery.store.activeIssueId ??
            undefined
          }
        />
      ) : (
        <NoIssuesComponent size={widget.overviewSize} />
      )}
    </Box>
  );
};

export { WidgetIssueOverviewList };
